import { useState, useRef, useEffect } from 'react';
import { aiAPI } from '../lib/api';
import { Brain, Send, Loader2 } from 'lucide-react';

export default function AIGuide() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text: userMsg }]);
    setLoading(true);

    try {
      const res = await aiAPI.guide(userMsg, null, sessionId);
      setSessionId(res.data.session_id);
      setMessages(prev => [...prev, { role: 'ai', text: res.data.response }]);
    } catch (err) {
      const detail = err.response?.data?.detail || 'AI service unavailable';
      setMessages(prev => [...prev, { role: 'error', text: detail }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 h-full flex flex-col" data-testid="ai-guide">
      <div className="flex items-center gap-3 mb-4">
        <Brain className="text-[#2F8A92]" size={20} />
        <h2 className="font-historical text-[#D4A84A] text-2xl font-light tracking-tight">
          Pattern Guide
        </h2>
      </div>
      <p className="font-mono text-xs text-[#52525B] mb-4">
        Ask about historical patterns, get context and clues. The guide will never solve the chain for you.
      </p>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto space-y-3 mb-4 min-h-0" data-testid="ai-messages">
        {messages.length === 0 && (
          <div className="glass-panel p-4 text-center">
            <p className="font-mono text-xs text-white/80">
              Welcome to the TSOGN Pattern Guide. Ask me about historical connections, cultural patterns, or definitions.
            </p>
          </div>
        )}
        {messages.map((msg, i) => (
          <div
            key={i}
            className={`animate-fade-in ${msg.role === 'user' ? 'ml-8' : 'mr-8'}`}
            style={{ animationDelay: `${i * 0.05}s` }}
          >
            <div className={`glass-panel p-4 ${
              msg.role === 'user' 
                ? 'border-l-2 border-[#2F8A92]' 
                : msg.role === 'error'
                  ? 'border-l-2 border-red-400'
                  : 'border-l-2 border-[#D4A84A]'
            }`}>
              <div className="font-hud text-[10px] tracking-widest uppercase mb-1" style={{
                color: msg.role === 'user' ? '#2F8A92' : msg.role === 'error' ? '#FF4444' : '#D4A84A'
              }}>
                {msg.role === 'user' ? 'YOU' : msg.role === 'error' ? 'ERROR' : 'PATTERN GUIDE'}
              </div>
              <p className="font-mono text-sm text-white/80 leading-relaxed whitespace-pre-wrap">
                {msg.text}
              </p>
            </div>
          </div>
        ))}
        {loading && (
          <div className="mr-8 animate-fade-in">
            <div className="glass-panel p-4 border-l-2 border-[#D4A84A]">
              <div className="flex items-center gap-2">
                <Loader2 className="animate-spin text-[#D4A84A]" size={14} />
                <span className="font-hud text-xs text-[#D4A84A] tracking-wider">ANALYZING PATTERNS...</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <div className="glass-panel p-3 flex items-center gap-3" data-testid="ai-input-area">
        <input
          data-testid="ai-input"
          className="hud-input flex-1"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSend()}
          placeholder="Ask about historical patterns..."
          disabled={loading}
        />
        <button
          data-testid="ai-send-btn"
          onClick={handleSend}
          disabled={loading || !input.trim()}
          className="text-[#2F8A92] hover:text-[#66FAFF] disabled:text-[#52525B] transition-all duration-300"
        >
          <Send size={18} />
        </button>
      </div>
    </div>
  );
}
